/**
 * Portal — Client view of a single cleaning's media folder.
 * Shows photos by checkpoint (kitchen, bath, …) + the before/after videos.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Images, PlayCircle, FolderOpen, Camera, Video } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import EmptyState from "@/components/EmptyState";

const POINT_LABELS = {
    keys_in: "Llaves al llegar",
    kitchen: "Cocina",
    main_bath: "Baño principal",
    living: "Salón",
    bedroom: "Dormitorios",
    trash: "Basura vaciada",
    keys_out: "Llaves al salir",
};

const TYPE_LABELS = {
    photo: "Foto",
    video_before: "Vídeo inicial",
    video_after: "Vídeo final",
};

export default function PortalCleaningMedia() {
    const { tid } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [lightbox, setLightbox] = useState(null); // { url, type }

    useEffect(() => {
        setLoading(true);
        api.get(`/portal/tasks/${tid}/media`)
            .then((r) => setData(r.data))
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [tid]);

    if (loading) return <div className="zentra-skeleton h-72" data-testid="portal-media-loading" />;
    if (error) return <p className="text-red-700 text-sm">{error}</p>;
    if (!data) return null;

    const media = data.media || [];
    const videoBefore = media.find((m) => m.type === "video_before");
    const videoAfter = media.find((m) => m.type === "video_after");
    const photosByPoint = {};
    const freePhotos = [];
    for (const m of media) {
        if (m.type !== "photo") continue;
        if (m.point_id) {
            if (!photosByPoint[m.point_id]) photosByPoint[m.point_id] = [];
            photosByPoint[m.point_id].push(m);
        } else {
            freePhotos.push(m);
        }
    }

    return (
        <div className="space-y-5" data-testid="portal-cleaning-media">
            <Link
                to={`/portal/propiedades/${data.property?.id || ""}`}
                className="inline-flex items-center gap-2 text-sm text-zentra-muted hover:text-zentra-ink"
            >
                <ArrowLeft size={14} /> Volver a la propiedad
            </Link>

            {/* Header */}
            <div className="zentra-card-static p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest font-semibold text-zentra-accent-strong inline-flex items-center gap-2">
                            <FolderOpen size={12} /> Carpeta de limpieza
                        </p>
                        <h1 className="font-display text-2xl font-bold text-zentra-ink mt-1">
                            {data.folder_name}
                        </h1>
                        <p className="text-sm text-zentra-muted mt-1">
                            {data.property?.name} · {data.task?.type} · estado: <strong>{data.task?.state}</strong>
                        </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md bg-zentra-bg border border-zentra-border">
                        <Images size={13} /> {media.length} archivos
                    </span>
                </div>
            </div>

            {media.length === 0 && (
                <EmptyState
                    icon={Images}
                    title="Sin contenido subido aún"
                    description="El equipo de limpieza aún no ha subido fotos ni vídeos para esta tarea. Volverá aquí cuando complete el servicio."
                />
            )}

            {/* Initial video */}
            {videoBefore && (
                <Section icon={Video} title="Vídeo inicial">
                    <VideoCard m={videoBefore} onOpen={() => setLightbox(videoBefore)} />
                </Section>
            )}

            {/* Photos by checkpoint */}
            {Object.keys(photosByPoint).length > 0 && (
                <Section icon={Camera} title="Fotos del servicio">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(photosByPoint).map(([pid, list]) =>
                            list.map((m) => (
                                <PhotoCard
                                    key={m.id}
                                    m={m}
                                    label={POINT_LABELS[pid] || pid}
                                    onOpen={() => setLightbox(m)}
                                />
                            )),
                        )}
                        {freePhotos.map((m) => (
                            <PhotoCard key={m.id} m={m} label="Foto extra" onOpen={() => setLightbox(m)} />
                        ))}
                    </div>
                </Section>
            )}

            {/* Final video */}
            {videoAfter && (
                <Section icon={Video} title="Vídeo final">
                    <VideoCard m={videoAfter} onOpen={() => setLightbox(videoAfter)} />
                </Section>
            )}

            {/* Lightbox */}
            {lightbox && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    onClick={() => setLightbox(null)}
                    data-testid="portal-lightbox"
                >
                    {lightbox.type === "photo" ? (
                        <img
                            src={lightbox.url}
                            alt="Foto limpieza"
                            className="max-h-[90vh] max-w-[90vw] object-contain rounded"
                        />
                    ) : (
                        <video
                            src={lightbox.url}
                            controls
                            autoPlay
                            className="max-h-[90vh] max-w-[90vw] rounded"
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div className="zentra-card-static p-5">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-zentra-muted inline-flex items-center gap-1.5 mb-3">
                <Icon size={12} /> {title}
            </p>
            {children}
        </div>
    );
}

function PhotoCard({ m, label, onOpen }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className="group relative aspect-[4/3] rounded-md overflow-hidden border border-zentra-border bg-zentra-bg hover:border-zentra-primary transition"
            data-testid={`portal-photo-${m.id}`}
        >
            <img
                src={m.thumb_url || m.url}
                alt={label}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition"
            />
            <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1.5 text-left">
                {label}
            </span>
        </button>
    );
}

function VideoCard({ m, onOpen }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className="group relative w-full aspect-video rounded-md overflow-hidden border border-zentra-border bg-zentra-ink hover:border-zentra-primary transition flex items-center justify-center"
            data-testid={`portal-video-${m.id}`}
        >
            <video src={m.url} className="w-full h-full object-cover opacity-80" muted preload="metadata" />
            <span className="absolute inset-0 flex items-center justify-center">
                <PlayCircle size={56} className="text-white drop-shadow" />
            </span>
        </button>
    );
}
